import { forwardRef, HTMLAttributes, Children, ReactElement, isValidElement, useEffect, useState, useMemo } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import {
  setOrderField,
  setOrderItems,
} from "../redux/slices/orderSlice";
import UserInfoField from "../components/UserInfoField";
import styled from "styled-components";
import { MenuItem, Autocomplete,TextField, Select, SelectChangeEvent, RadioGroup, Radio, FormControlLabel} from "@mui/material";
import { clearCart } from "../redux/slices/cartSlice";
import  { processBooksData } from "../services/booksService";
import { OptionType, WarehouseType } from "../types/types";
import { useNavigate } from "react-router-dom";

const Container = styled.main`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: white;
  padding: 20px;
`;

const FormWrapper = styled.div`
  width: 100%;
  max-width: 500px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Input = styled.input`
  padding: 10px;
  border-radius: 10px;
  border: 1px solid #ccc;
  width: 100%;
  max-width: 500px;
  font-size: 16px;
  outline: none;
  margin-bottom: 16px;

  &:focus {
    border-color: #3b6255;
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #eee;
  margin-top: 32px;
  margin-bottom: 32px;
  width: 100%;
  max-width: 500px;
`;

const Title = styled.h2`
  margin-top: 24px;
  font-size: 28px;
  font-weight: 800;
  color: #3b6255;
  margin-bottom: 24px;
`;

const Section = styled.section`
  width: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Label = styled.label`
  font-family: "IBM Plex Mono";
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 6px;
  display: block;
  color: #000;
`;

const Button = styled.button`
  background: linear-gradient(135deg, #3b6255 0%, #2d4a3d 100%);
  color: white;
  padding: 14px 28px;
  border-radius: 12px;
  font-weight: 700;
  font-size: 16px;
  cursor: pointer;
  border: none;
  align-self: center;
  transition: all 0.2s ease;
  margin-bottom: 40px;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(59, 98, 85, 0.3);
  }
`;

const ItemsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 20px;
  width: 100%;
`;

const ItemCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border: 1px solid #ddd;
  border-radius: 12px;
  background: #fafafa;
  font-family: "IBM Plex Mono", monospace;
`;

const ItemImage = styled.img`
  width: 50px;
  height: 70px;
  object-fit: cover;
  border-radius: 6px;
`;

const ItemInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const ItemTitle = styled.span`
  font-size: 16px;
  font-weight: 600;
`;

const ItemAuthor = styled.span`
  font-size: 14px;
  color: #666;
`;

const ItemPrice = styled.div`
  font-weight: 700;
  font-size: 16px;
  color: #3b6255;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: start;
  width: 100%;
  margin-top: 24px;
  margin-bottom: 40px;
  border-radius: 12px;
  color: #2d4a3d;
  font-weight: 700;
  font-size: 18px;
`;

const commonSelectStyles = {
  borderRadius: "10px",
  width: "100%",
  maxWidth: "500px",
  fontSize: "16px",
  outline: "none",
  marginBottom: "16px",
  height: "43px", 
  "& .MuiOutlinedInput-notchedOutline": { 
    borderColor: "#ccc" 
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { 
    borderColor: "#3b6255" 
  },
  "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#3b6255 !important",
    borderWidth: "2px",
  },
  "& .MuiSelect-select": {
    height: "43px",
    lineHeight: "43px",
    paddingTop: 0,
    paddingBottom: 0,
    display: "flex",
    alignItems: "center",
  },
  "& .MuiOutlinedInput-root": {
    height: "43px",
    borderRadius: "10px",
  },
  '& .MuiAutocomplete-option': { 
    '&:hover': { 
      backgroundColor: 'rgba(59, 98, 85, 0.08) !important', 
      color: '#3b6255'
    }
  },
  '& .MuiFormLabel-root': {
    lineHeight: "1"
  }
};

const radioStyles = {
  color: '#ccc', 
  '&.Mui-checked': {
    color: '#3b6255',
  },
  '&:hover': {
    backgroundColor: 'rgba(59, 98, 85, 0.04)',
  },
};

const labelStyles = {
  '& .MuiFormControlLabel-label': {
    fontSize: '16px',
    color: '#333',
    fontFamily: 'inherit',
  }
};

const OrderPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const order = useSelector((state: RootState) => state.order);
  const profile = useSelector((state: RootState) => state.profile);
  const cart = useSelector((state: RootState) => state.cart);
  const { accessToken } = useSelector((state: RootState) => state.auth);
  const customerId = useSelector((state: RootState) => state.auth.customer_id);

  const [selectedCity, setSelectedCity] = useState<OptionType | null>(null);
  const [cityInput, setCityInput] = useState("");
  const [cities, setCities] = useState<OptionType[]>([]);
  const [courierDeliveryAddress, setDeliveryAddress] = useState<string>("");

  const [selectedWarehouse, setSelectedWarehouse] = useState<OptionType | null>(null);
  const [warehouseInput, setWarehouseInput] = useState("");
  const [warehouses, setWarehouses] = useState<OptionType[]>([]);
  const apiKey = "5cf74478d96609be2b2e3ccd5438dc1a";
  const [deliveryType, setDeliveryType] = useState('branch');

  const { branches, postomats } = useMemo(() => {
    const postomatsTypesList: string[] = ['95dc212d-479c-4ffb-a8ab-8c1b9073d0bc', 'f9316480-5f2d-425d-bc2c-ac7cd29decf0'];
    const result: { branches: OptionType[]; postomats: OptionType[] } = {
        branches: [],
        postomats: []
      };

      if (!warehouses) return result;

    (warehouses).forEach((warehouse: OptionType): void => {
      postomatsTypesList.includes(warehouse.type!) ? result.postomats.push(warehouse) : result.branches.push(warehouse)
    });
    return result;
  }, [warehouses]);

  const VirtuosoListbox = forwardRef<HTMLDivElement, HTMLAttributes<HTMLElement>>(
    (props, ref) => {
      const { children, ...other } = props;
      const itemData = Children.toArray(children);
      const [listHeight, setListHeight] = useState(0);
      const MAX_HEIGHT = 300;
      return (
        <div 
          ref={ref} 
          onMouseDown={(e) => e.preventDefault()}
          style={{ 
            minHeight: itemData.length > 0 ? 48 : 0,
            height: Math.min(listHeight, MAX_HEIGHT),
            transition: 'height 0.2s ease-out'
          }}
        >
          <Virtuoso
            style={{ height: '100%', width: '100%', background: 'inherit' }}
            data={itemData}
            totalCount={itemData.length}
            totalListHeightChanged={setListHeight}
            
            itemContent={(_index, item) => {
              if (!isValidElement(item)) return null;
              const element = item as ReactElement<any>;
              const elementProps = element.props;

              return (
                <div 
                  {...elementProps} 
                  style={{ 
                    ...elementProps.style, 
                    height: 'auto',
                    minHeight: '48px',
                    margin: 0,
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 10px',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(59, 98, 85, 0.08)';
                    e.currentTarget.style.color = '#3b6255';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = 'inherit';
                  }}
                >
                  {elementProps.children}
                </div>
              );
            }}
          />
        </div>
      );
    }
  );

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BASE_API_URL}/profile/me`, {
          credentials: 'include',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        });

        if (!res.ok) throw new Error('Failed to fetch profile');

        const data = await res.json();
        if (!order.firstName) {
          dispatch(setOrderField({ field: "firstName", value: data.first_name }));
          dispatch(setOrderField({ field: "lastName", value: data.second_name }));
          dispatch(setOrderField({ field: "email", value: data.email }));
          dispatch(setOrderField({ field: "phone", value: data.phone }));
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchProfile();
    if (cart.items?.length > 0 && order.items.length < 1) {
      processBooksData(cart.items).then(items => {
        const formattedItems = items.map((item: any) => ({
          id: item.book_id,
          name: item.book_title,
          price: item.book_price,
          quantity: item.book_quantity,
          author: item.author_name,
          cover: item.book_cover_path
        }));
        dispatch(setOrderItems(formattedItems));
      });
    }
  }, [profile, cart, dispatch, accessToken, order.firstName]);

  useEffect(() => {
    const fetchCities = async () => {
      if (cityInput.length < 3) return;
      try {
        const response = await fetch("https://api.novaposhta.ua/v2.0/json/", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            apiKey,
            modelName: "Address",
            calledMethod: "getCities",
            methodProperties: { FindByString: cityInput }
          })
        });

        const data = await response.json();
        setCities(
          data.data.map((city: any) => ({
            value: city.Ref,
            label: `${city.Description}, ${city.AreaDescription} обл.`
          }))
        );
      } catch (err) {
        console.error("Ошибка при получении городов:", err);
      }
    };

    fetchCities();
  }, [cityInput]);

  useEffect(() => {
    const fetchWarehouses = async () => {
      if (!selectedCity) return;
      try {
        const response = await fetch("https://api.novaposhta.ua/v2.0/json/", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            apiKey,
            modelName: "Address",
            calledMethod: "getWarehouses",
            methodProperties: {
              CityRef: selectedCity.value,
              "Limit": "0"
            }
          })
        });

        const data = await response.json();
        setWarehouses(
          data.data.map((wh: WarehouseType) => ({
            value: wh.Ref,
            label: wh.Description,
            type: wh.TypeOfWarehouse
          }))
        );
      } catch (err) {
        console.error("Ошибка при получении отделений:", err);
      }
    };

    fetchWarehouses();
  }, [warehouseInput, selectedCity]);

  const total = order.items.reduce((sum, item) => sum + item.price * item.quantity, 0);

  const handleSubmit = async () => {
    const nameRegex = /^[А-ЯІЇЄҐа-яіїєґ'’\- ]+$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\+380\d{9}$/;

    if (!nameRegex.test(order.firstName) || !nameRegex.test(order.lastName)) {
      alert('Ім’я та прізвище повинні містити лише українські літери');
      return;
    }
    if (!emailRegex.test(order.email)) {
      alert('Некоректний email');
      return;
    }
    if (!phoneRegex.test(order.phone)) {
      alert('Телефон має бути у форматі +380XXXXXXXXX');
      return;
    }

    if (order.items.length === 0) {
      alert('Кошик порожній!');
      return;
    }

    if (!order.firstName || !order.lastName || !order.email || !order.phone) {
      alert("Будь ласка, заповніть усі обовʼязкові поля!");
      return;
    }

    if (!order.deliveryMethod) {
      alert("Будь ласка, оберіть метод доставки!");
      return;
    }

    if (!order.address) {
      alert("Будь ласка, додайте адресу!");
      return;
    }

    if (!order.paymentMethod) {
      alert("Будь ласка, оберіть метод оплати!");
      return;
    }

    const deliveryInfo =
      order.deliveryMethod === "courier"
        ? {
            address: order.address,
          }
        : { address: order.address };

    const orderId = `${Date.now().toString(36).toUpperCase()}${Math.floor(Math.random() * 10000).toString().padStart(4, "0")}`.substring(0, 20);
    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_API_URL}/order/ask-payment?customerId=${customerId}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          orderId: `ORDER-${orderId}`,
          amount: order.items.reduce((sum, item) => sum + item.price * item.quantity, 0),
          description: `Оплата замовлення #${orderId}`,
          customerId,
          email: order.email,
          phone: order.phone,
          paymentMethod: order.paymentMethod,
          deliveryMethod: order.deliveryMethod,
          address: deliveryInfo,
          items: order.items
        }),
      });

      if (!response.ok) {
        throw new Error('Помилка при створенні замовлення');
      }

      dispatch(clearCart());

      if (order.paymentMethod === 'after-delivery') {
        alert('Замовлення успішно сформоване! Оплата при отриманні.');
        navigate("/");
        return;
      }

      const html = await response.text();

      const paymentWindow = window.open('', '_blank');
      if (paymentWindow) {
        paymentWindow!.document.write(html);
        paymentWindow!.document.close();
      }
    } catch (err: any) {
      console.error(err);
      alert(err.message || 'Помилка при створенні платежу');
    }
  };
    
  return (
    <Container>
      <FormWrapper>
        <Title>Оформлення замовлення</Title>

        <Section>
          <UserInfoField
            label="Прізвище"
            value={order.lastName || profile.lastName}
            onChange={(e) =>
              dispatch(setOrderField({ field: "lastName", value: e.target.value }))
            }
          />
          <UserInfoField
            label="Ім’я"
            value={order.firstName || profile.firstName}
            onChange={(e) =>
              dispatch(setOrderField({ field: "firstName", value: e.target.value }))
            }
          />
          <UserInfoField
            disabled
            label="Email"
            value={order.email || profile.email}
          />
          <UserInfoField
            label="Телефон"
            value={order.phone || profile.phone}
            onChange={(e) =>
              dispatch(setOrderField({ field: "phone", value: e.target.value }))
            }
          />
        </Section>

        <Divider />

        <Section>
          <Label>Метод доставки</Label>
          <Select
            value={order.deliveryMethod}
            onChange={(e: SelectChangeEvent) => 
              dispatch(setOrderField({ field: "deliveryMethod", value: e.target.value }))
            }
            sx={commonSelectStyles}
          >
            <MenuItem value="nova-poshta">Нова Пошта</MenuItem>
            {/*<MenuItem value="courier">Курʼєрська доставка</MenuItem>*/}
          </Select>

          {order.deliveryMethod === "courier" && (
            <>
              <Label>Адреса доставки</Label>
              <UserInfoField
                label=""
                value={order.address}
                onChange={(e) =>
                  dispatch(setOrderField({ field: "address", value: e.target.value }))
                }
              />
            </>
          )}

          {order.deliveryMethod === "nova-poshta" && (
            <>
                <Label>Оберіть місто</Label>
                <Autocomplete
                  options={cities}
                  getOptionLabel={(option) => option.label}
                  value={selectedCity}
                  onChange={(event, newValue) => {
                    if (!newValue) {
                      dispatch(setOrderField({ field: "address", value: ''}));
                      setDeliveryAddress('');
                    }
                    setSelectedWarehouse(null);
                    setSelectedCity(newValue);
                  }}
                  inputValue={cityInput}
                  onInputChange={(event, newInputValue) => setCityInput(newInputValue)}
                  slotProps={{
                    listbox: {
                      component: VirtuosoListbox,
                    },
                  }}
                  noOptionsText={cityInput.length === 0
                  ? "Виберіть своє місто" 
                  : "Місто не знайдено"}
                  renderInput={(params) => 
                  <TextField {...params} sx={commonSelectStyles} />}
                />
                {selectedCity && (<>
                  <RadioGroup
                    row
                    value={deliveryType}
                    onChange={(e) => {
                      setDeliveryType(e.target.value);
                      setSelectedWarehouse(null);
                      dispatch(setOrderField({ field: "address", value: ''}))
                      setDeliveryAddress('');
                      console.log('state', order)
                    }}
                  >
                    <FormControlLabel value="branch" control={<Radio sx={radioStyles} />} label="Відділення" />
                    <FormControlLabel value="postomat" control={<Radio sx={radioStyles} />} label="Поштомат" />
                    <FormControlLabel value="np-courier" control={<Radio sx={radioStyles} />} label="Кур'єр Нової Пошти" />
                  </RadioGroup>
                  {deliveryType === "np-courier" ? <>
                    <TextField
                      label="Адреса доставки"
                      value={courierDeliveryAddress}
                      sx={commonSelectStyles}
                      onChange={(e) => 
                        setDeliveryAddress(e.target.value)
                      }
                      onBlur={()=>
                        dispatch(setOrderField({ field: "address", value: `${selectedCity?.label}, ${courierDeliveryAddress}` }))
                      }
                    />
                    </> : <>
                    <Autocomplete
                      options={deliveryType === 'branch' ? branches : postomats}
                      getOptionLabel={(option) => option.label}
                      value={selectedWarehouse}
                      onChange={(event, newValue) => {
                          setSelectedWarehouse(newValue);
                          if (newValue) {
                            dispatch(setOrderField({
                              field: "address",
                              value: `${selectedCity?.label}, ${newValue.label}`
                            }));
                          } else {
                            dispatch(setOrderField({ field: "address", value: "" }));
                          }
                        }}
                      inputValue={warehouseInput}
                      disabled={!selectedCity}
                      slotProps={{
                        listbox: {
                          component: VirtuosoListbox,
                        }
                      }}
                      disableListWrap
                      onInputChange={(event, newInputValue) => setWarehouseInput(newInputValue)}
                      noOptionsText={warehouseInput.length === 0
                      ? "Введіть адресу або номер відділення"
                      : "За вашим запитом нічого не знайдено"}
                      renderInput={(params) =>
                      <TextField {...params} sx={commonSelectStyles} placeholder='Введіть адресу або номер відділення' />}
                    />
                  </>}
                </>)}
            </>
          )}
        </Section>

        <Divider />

        <Section>
          <Label>Метод оплати</Label>
          <Select
              value={order.paymentMethod}
              onChange={(e: SelectChangeEvent) => 
                dispatch(setOrderField({ field: "paymentMethod", value: e.target.value }))
              }
              sx={commonSelectStyles}
            >
              <MenuItem value="card">Оплата карткою</MenuItem>
              <MenuItem value="after-delivery">Оплата при отриманні</MenuItem>
            </Select>
        </Section>

        <ItemsWrapper>
          {order.items.map((item) => (
            <ItemCard key={item.id}>
              <ItemImage src={item.cover || "/placeholder.png"} alt={item.name} />
              <ItemInfo>
                <ItemTitle>{item.name}</ItemTitle>
                <ItemAuthor>{item.author}</ItemAuthor>
                <ItemAuthor>x{item.quantity}</ItemAuthor>
              </ItemInfo>
              <ItemPrice>{item.price} грн</ItemPrice>
            </ItemCard>
          ))}
        </ItemsWrapper>

        <Footer>Разом: {total} грн</Footer>

        <Button onClick={handleSubmit}>Підтвердити замовлення</Button>
      </FormWrapper>
    </Container>
  );
};

export default OrderPage;
